import React, { useEffect, useRef, useState } from "react";
import Select, { type Theme } from "react-select";
import { useNavigate, useOutletContext } from "react-router";
import {
  IAssessment,
  IAssessmentSearchRequest,
  ICmsSubject,
  getCmsAssessments,
  getCmsTeacherSubjects,
} from "../../../../api/crmContent.service";
import {
  EditIcon,
  FilterIcon,
  PreviewIcon,
  ResetIcon,
} from "../../../../assets/icons/index";
import PreviewModal from "../../questions/previewModal";
import { ISelectElement } from "../../../../model/interface/keyValuePair";
import Loader from "../../../shared/loader/loader";
import Pagination from "../../../shared/common/pagination";
import SelectPageSize from "../../../shared/common/pageSizeDropdown";
import { SortIcon } from "../../../shared/common/sortIcon";
import { EmptyResults } from "../../../shared/common/emptyResults";
import { InitialsBadge } from "../../../shared/common/initialsBadge";
import Constant from "../../../../utils/constant";
import { IUserContentOutletContext } from "./UserContentLayout";
import { toast } from "react-toastify";

const defaultPageSize = 25;
const defaultPageNumber = 1;
const defaultOrderByColumn = "CreateDate";
const defaultSortByDesc = true;

const reactSelectTheme = (theme: Theme): Theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: "#B3B3B3",
    primary: "#041E42",
  },
});

export const Assessments: React.FC = () => {
  const { userId } = useOutletContext<IUserContentOutletContext>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [assessments, setAssessments] = useState<Array<IAssessment>>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [previewId, setPreviewId] = useState<string>("");
  const [englishPreviewId, setEnglishPreviewId] = useState<string>("");
  const [spanishPreviewId, setSpanishPreviewId] = useState<string>("");

  const [subjects, setSubjects] = useState<Array<ISelectElement>>([]);
  const [selectedSubject, setSelectedSubject] = useState<ISelectElement | null>(
    null
  );
  const [nameFilter, setNameFilter] = useState<string>("");
  const [codeFilter, setCodeFilter] = useState<string>("");

  const [pageSize, setPageSize] = useState<number>(defaultPageSize);
  const [pageNumber, setPageNumber] = useState<number>(defaultPageNumber);
  const [orderByColumn, setOrderByColumn] =
    useState<string>(defaultOrderByColumn);
  const [sortByDesc, setSortByDesc] = useState<boolean>(defaultSortByDesc);

  const paginationRef = useRef<{ onReset: () => void }>(null);
  const pageSizeRef = useRef<{ onReset: () => void }>(null);
  const pageNumberInitial = useRef<boolean>(true);
  const pageSizeInitial = useRef<boolean>(true);

  const buildRequest = (
    overrides?: Partial<IAssessmentSearchRequest>
  ): IAssessmentSearchRequest => {
    return {
      teacherId: userId ?? 0,
      name: nameFilter || undefined,
      code: codeFilter || undefined,
      subjectId: selectedSubject ? Number(selectedSubject.value) : undefined,
      pageSize,
      pageNumber,
      orderByColumn,
      sortByDesc,
      ...overrides,
    };
  };

  const search = (request: IAssessmentSearchRequest) => {
    setLoading(true);
    getCmsAssessments(request)
      .then((response) => {
        setAssessments(response.data.data);
        setTotalRecords(response.data.totalRecords);
        setTotalPages(Math.ceil(response.data.totalRecords / request.pageSize));
      })
      .catch(() => toast.error("Failed to load assessments."))
      .finally(() => setLoading(false));
  };

  const bindSubjects = () => {
    if (!userId) return;
    getCmsTeacherSubjects(userId).then((response) => {
      const options: Array<ISelectElement> = (response.data ?? []).map(
        (subject: ICmsSubject) => ({
          value: String(subject.subjectId),
          label: subject.name,
        })
      );
      setSubjects(options);
    });
  };

  useEffect(() => {
    bindSubjects();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    search({
      teacherId: userId,
      pageSize: defaultPageSize,
      pageNumber: defaultPageNumber,
      orderByColumn: defaultOrderByColumn,
      sortByDesc: defaultSortByDesc,
    });
  }, [userId]);

  useEffect(() => {
    if (pageNumberInitial.current) {
      pageNumberInitial.current = false;
      return;
    }
    search(buildRequest({ pageNumber }));
  }, [pageNumber]);

  useEffect(() => {
    if (pageSizeInitial.current) {
      pageSizeInitial.current = false;
      return;
    }
    paginationRef.current?.onReset();
    search(buildRequest({ pageNumber: defaultPageNumber, pageSize }));
  }, [pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    paginationRef.current?.onReset();
    pageSizeRef.current?.onReset();
    search(
      buildRequest({
        pageNumber: defaultPageNumber,
        pageSize: defaultPageSize,
      })
    );
  };

  const handleReset = () => {
    setSelectedSubject(null);
    setNameFilter("");
    setCodeFilter("");
    paginationRef.current?.onReset();
    pageSizeRef.current?.onReset();
    setOrderByColumn(defaultOrderByColumn);
    setSortByDesc(defaultSortByDesc);
    search({
      teacherId: userId ?? 0,
      pageSize: defaultPageSize,
      pageNumber: defaultPageNumber,
      orderByColumn: defaultOrderByColumn,
      sortByDesc: defaultSortByDesc,
    });
  };

  const handleSortBy = (column: string) => {
    const newSortByDesc = orderByColumn === column ? !sortByDesc : false;
    setOrderByColumn(column);
    setSortByDesc(newSortByDesc);
    paginationRef.current?.onReset();
    search(
      buildRequest({
        orderByColumn: column,
        sortByDesc: newSortByDesc,
        pageNumber: defaultPageNumber,
      })
    );
  };

  const openPreview = (assessment: IAssessment) => {
    setPreviewId(assessment.learnosityIdEnglish ?? "");
    setEnglishPreviewId(assessment.learnosityIdEnglish ?? "");
    setSpanishPreviewId(assessment.learnosityIdSpanish ?? "");
    setPreviewOpen(true);
  };

  const handleCreateAssessment = () => {
    navigate(
      Constant.crmUserContentAssessmentCreate.replace(
        ":userid",
        String(userId ?? "")
      )
    );
  };

  const renderSortableHeader = (
    label: string,
    column: string,
    className?: string
  ) => (
    <th
      scope="col"
      className={`px-3 py-2 font-light text-sm text-left text-white dark:text-white tracking-wider${
        className ? ` ${className}` : ""
      }`}
    >
      <span className="flex items-center">
        {label}
        <span
          className="cursor-pointer ml-1 inline-flex"
          onClick={() => handleSortBy(column)}
        >
          <SortIcon
            direction={
              orderByColumn === column ? (sortByDesc ? "desc" : "asc") : "none"
            }
            className="w-2.5 h-4"
          />
        </span>
      </span>
    </th>
  );

  return (
    <>
      {loading && <Loader />}
      <PreviewModal
        show={previewOpen}
        changeValue={(val: boolean) => setPreviewOpen(val)}
        activityRef={previewId}
        activityIdPreview={previewId}
        spanishActivityRef={spanishPreviewId}
        englishActivityRef={englishPreviewId}
        setPreviewId={setPreviewId}
      />
      <div className="flex-1 overflow-auto">
        <div className="h-auto min-h-half w-full px-8 py-6">
          <div className="relative py-1">
            <form onSubmit={handleSearch}>
              <div className="pt-6 border-b pb-6">
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-3 sm:col-span-3">
                    <label className="text-sm text-gray-700">Name</label>
                    <input
                      type="text"
                      className="mt-1 block w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-color"
                      value={nameFilter}
                      onChange={(e) => setNameFilter(e.target.value)}
                    />
                  </div>

                  <div className="col-span-3 sm:col-span-3">
                    <label className="text-sm text-gray-700">Code</label>
                    <input
                      type="text"
                      className="mt-1 block w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-color"
                      value={codeFilter}
                      onChange={(e) => setCodeFilter(e.target.value)}
                    />
                  </div>

                  <div className="col-span-3 sm:col-span-3">
                    <label className="text-sm text-gray-700">Subject</label>
                    <Select
                      className="w-full mt-1 text-sm"
                      options={subjects}
                      onChange={(option) =>
                        setSelectedSubject(option as ISelectElement | null)
                      }
                      value={selectedSubject}
                      isClearable={true}
                      isSearchable={true}
                      theme={reactSelectTheme}
                    />
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <div className="flex">
                    <button
                      type="submit"
                      className="mt-4 inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
                    >
                      <FilterIcon className="w-3 h-3" />
                      <span>Search</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleReset}
                      className="mt-4 py-1.5 px-4 inline-flex ml-3 items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color"
                    >
                      <ResetIcon className="w-3 h-3" />
                      <span>Reset</span>
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleCreateAssessment}
                    className="mt-4 inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
                  >
                    <span>Create Assessment</span>
                  </button>
                </div>
              </div>
            </form>

            <div id="dataContainer" className="mt-4">
              {assessments.length === 0 ? (
                <EmptyResults
                  title="No matching assessments"
                  subtitle="Adjust your filters and try again."
                />
              ) : (
                <div className="flex flex-col mt-1">
                  <div className="-my-2 relative sm:-mx-6 lg:-mx-8">
                    <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                      <div className="shadow border-b border-gray-200 sm:rounded-lg">
                        <table className="min-w-full table-fixed divide-y divide-gray-200">
                          <thead className="bg-primary-color">
                            <tr>
                              <th
                                scope="col"
                                className="px-3 py-2 w-20 font-light text-sm text-left text-white dark:text-white tracking-wider"
                              >
                                Id
                              </th>
                              {renderSortableHeader(
                                "Created On",
                                "CreateDate",
                                "w-32"
                              )}
                              <th
                                scope="col"
                                className="px-3 py-2 w-28 font-light text-sm text-left text-white dark:text-white tracking-wider"
                              >
                                Code
                              </th>
                              {renderSortableHeader("Name", "name")}
                              <th
                                scope="col"
                                className="px-3 py-2 font-light text-sm text-left text-white dark:text-white tracking-wider"
                              >
                                Subject
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-2 w-16 font-light text-sm text-center text-white dark:text-white tracking-wider"
                              >
                                Status
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-2 w-20 font-light text-sm text-center text-white dark:text-white tracking-wider"
                              >
                                Questions
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-2 w-24 font-light text-sm text-center text-white dark:text-white tracking-wider"
                              >
                                Owner
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-2 w-24 font-light text-sm text-center text-white dark:text-white tracking-wider"
                              >
                                Author
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-2 w-20 font-light text-sm text-center text-white dark:text-white tracking-wider rounded-tr-lg"
                              >
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {assessments.map((assessment, index) => (
                              <tr
                                key={assessment.assessmentId}
                                className={
                                  index % 2 === 0 ? "bg-white" : "bg-gray-100"
                                }
                              >
                                <td className="px-3 py-2 text-sm font-normal text-gray-700">
                                  {assessment.assessmentId}
                                </td>
                                <td className="px-3 py-2 text-sm font-normal text-gray-500 whitespace-nowrap">
                                  {assessment.createdDate
                                    ? new Date(
                                        assessment.createdDate
                                      ).toLocaleDateString()
                                    : ""}
                                </td>
                                <td className="px-3 py-2 text-sm font-normal text-gray-500">
                                  {assessment.code}
                                </td>
                                <td
                                  className={`px-3 py-2 text-sm font-normal text-gray-700${
                                    assessment.assessmentStatusId === 3
                                      ? " italic"
                                      : ""
                                  }`}
                                >
                                  {assessment.name}
                                </td>
                                <td className="px-3 py-2 text-sm font-normal text-gray-500">
                                  {assessment.subject}
                                </td>
                                <td className="px-3 py-2 text-sm text-center font-normal text-gray-500">
                                  <span
                                    className={`inline-block w-2 h-2 rounded-full ${
                                      assessment.assessmentStatusId === 1
                                        ? "bg-green-500"
                                        : assessment.assessmentStatusId === 3
                                        ? "bg-gray-400"
                                        : "bg-yellow-500"
                                    }`}
                                    title={
                                      assessment.assessmentStatusId === 1
                                        ? "New"
                                        : assessment.assessmentStatusId === 3
                                        ? "Draft"
                                        : "In Progress"
                                    }
                                  />
                                </td>
                                <td className="px-3 py-2 text-sm text-center font-normal text-gray-500">
                                  {assessment.questionsCount}
                                </td>
                                <td className="px-3 py-2 text-sm text-center font-normal text-gray-500">
                                  <InitialsBadge
                                    firstName={assessment.ownerFirstName}
                                    lastName={assessment.ownerLastName}
                                    userId={assessment.ownerUserId}
                                  />
                                </td>
                                <td className="px-3 py-2 text-sm text-center font-normal text-gray-500">
                                  <InitialsBadge
                                    firstName={
                                      assessment.authorFirstName ?? undefined
                                    }
                                    lastName={
                                      assessment.authorLastName ?? undefined
                                    }
                                    userId={assessment.authorUserId}
                                  />
                                </td>
                                <td className="px-3 py-2 text-sm text-center font-normal text-gray-500">
                                  <div className="flex justify-center gap-2">
                                    <EditIcon
                                      title="Edit assessment"
                                      onClick={() =>
                                        navigate(
                                          Constant.crmUserContentAssessmentEdit
                                            .replace(":userid", String(userId))
                                            .replace(
                                              ":assessmentId",
                                              String(assessment.assessmentId)
                                            )
                                        )
                                      }
                                      className="h-5 w-5 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300"
                                    />
                                    <PreviewIcon
                                      title="Preview assessment"
                                      onClick={() => openPreview(assessment)}
                                      className="h-5 w-5 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300"
                                    />
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center mt-2">
                    {totalPages > 1 && (
                      <Pagination
                        pages={totalPages}
                        setPageNumber={setPageNumber}
                        ref={paginationRef}
                      />
                    )}
                    <span className="ml-auto">
                      <span className="text-sm pr-4 ml-auto text-left-700">
                        Showing {pageNumber} to{" "}
                        {totalRecords < pageSize ? totalRecords : pageSize} of{" "}
                        {totalRecords} results
                      </span>
                      <SelectPageSize
                        setPageSize={setPageSize}
                        type={"second"}
                        ref={pageSizeRef}
                        defaultPage={defaultPageSize}
                      />
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
